import type { AdaptOptions, PuzzleClues } from '../types.js';
import { CellValue } from '../types.js';
import { cluesFromLine } from '../clues.js';
import { fullSettle } from '../solver/grid-solver.js';

/* ─── Types shared with worker pools ─────────────────────────────────────── */

export interface AdaptScoreContext {
  /** Current working grid (boolean). Must match settleGrid's dimensions. */
  workGrid: boolean[][];
  /** Current full clue set — row r and col c are updated incrementally per trial. */
  currentClues: PuzzleClues;
  /** Result of fullSettle(workGrid, currentClues) — trials warm-start from this. */
  settleGrid: CellValue[][];
  alpha: number;
  beta: number;
  gamma: number;
  sourceImage?: number[][];
  existingPuzzles?: boolean[][][];
}

export interface AdaptScoreBatchRequest {
  workGrid: boolean[][];
  currentClues: PuzzleClues;
  settleGrid: CellValue[][];
  candidates: [number, number][];
  alpha: number;
  beta: number;
  gamma: number;
  sourceImage?: number[][];
  existingPuzzles?: boolean[][][];
}

export interface AdaptScoreBatchResponse {
  /** One score per candidate, same order. */
  scores: number[];
}

export type AdaptScoreBatchFn = (
  req: AdaptScoreBatchRequest,
) => Promise<AdaptScoreBatchResponse>;

/* ─── Public API ─────────────────────────────────────────────────────────── */

/**
 * Modifies a puzzle grid to make it solvable by line-logic alone.
 * Iteratively flips white pixels to black, choosing the pixel that
 * minimizes remaining unknowns (weighted by source image resemblance
 * and diversity from existing puzzles).
 *
 * Based on the Adapt algorithm from Batenburg et al. (2009).
 *
 * Performance: trials use incremental clue recompute (only the flipped
 * row and column) and warm-start fullSettle from the outer iteration's
 * settled grid with only the flipped line marked dirty. Results are
 * bit-identical to a naive implementation — line-logic propagation is
 * a monotone fixpoint under constraint tightening, so warm-starting
 * from a prior fully-settled state converges to the same grid.
 */
export function adaptPuzzle(
  grid: boolean[][],
  clues: PuzzleClues,
  options?: AdaptOptions,
): { grid: boolean[][]; clues: PuzzleClues; sweepCount: number } {
  return runAdaptSync(grid, clues, options);
}

/**
 * Async variant of `adaptPuzzle` that delegates candidate scoring to
 * an external batch function (typically backed by a worker pool). The
 * orchestration is identical; only the inner `for each candidate`
 * scoring loop is parallelized.
 */
export async function adaptPuzzleAsync(
  grid: boolean[][],
  clues: PuzzleClues,
  options: AdaptOptions | undefined,
  scoreBatch: AdaptScoreBatchFn,
): Promise<{ grid: boolean[][]; clues: PuzzleClues; sweepCount: number }> {
  return runAdaptAsync(grid, clues, options, scoreBatch);
}

/**
 * Score a single candidate flip. Exported so worker pools can reuse
 * the same scoring logic without duplicating the algorithm.
 *
 * Mutates `ctx.workGrid[r][c]` transiently and restores it before returning.
 */
export function scoreAdaptCandidate(
  r: number,
  c: number,
  ctx: AdaptScoreContext,
): number {
  const {
    workGrid,
    currentClues,
    settleGrid,
    alpha,
    beta,
    gamma,
    sourceImage,
    existingPuzzles,
  } = ctx;
  const rows = workGrid.length;

  // Flip (r,c) black, recompute clues incrementally (only row r and col c change)
  workGrid[r][c] = true;
  const newRowClue = cluesFromLine(workGrid[r]);
  const colLine = new Array<boolean>(rows);
  for (let i = 0; i < rows; i++) colLine[i] = workGrid[i][c];
  const newColClue = cluesFromLine(colLine);

  const trialClues: PuzzleClues = {
    rows: currentClues.rows.map((x, i) => (i === r ? newRowClue : x)),
    cols: currentClues.cols.map((x, i) => (i === c ? newColClue : x)),
  };

  // Warm-start: seed with the outer iteration's settled grid + (r,c) forced.
  // Under tighter row-r / col-c clues, every previously-deduced cell remains
  // valid (monotone under constraint tightening), and (r,c) is known-filled.
  const trialSeed = cloneCellGrid(settleGrid);
  trialSeed[r][c] = CellValue.Filled;

  // Only rows/cols with changed clues need an initial dirty mark — everything
  // else is already at its fixpoint under unchanged clues.
  const trialResult = fullSettle(trialSeed, trialClues, {
    rows: [r],
    cols: [c],
  });

  let score = alpha * trialResult.remainingUnknowns;

  if (sourceImage && sourceImage[r] && sourceImage[r][c] !== undefined) {
    score += beta * sourceImage[r][c];
  }

  if (existingPuzzles && existingPuzzles.length > 0) {
    let diversityPenalty = 0;
    for (const existing of existingPuzzles) {
      if (existing[r] && existing[r][c]) diversityPenalty++;
    }
    score += gamma * diversityPenalty;
  }

  workGrid[r][c] = false;
  return score;
}

/* ─── Internal orchestration ─────────────────────────────────────────────── */

interface AdaptState {
  rows: number;
  cols: number;
  workGrid: boolean[][];
  currentClues: PuzzleClues;
  warmStartSeed: CellValue[][] | null;
  warmStartDirty: { rows?: number[]; cols?: number[] } | undefined;
  alpha: number;
  beta: number;
  gamma: number;
  sourceImage?: number[][];
  existingPuzzles?: boolean[][][];
}

type AdaptStep =
  | { kind: 'done'; sweepCount: number }
  | {
      kind: 'score';
      candidates: [number, number][];
      settleGrid: CellValue[][];
      sweepCount: number;
    };

function initState(
  grid: boolean[][],
  clues: PuzzleClues,
  options: AdaptOptions | undefined,
): AdaptState {
  const rows = grid.length;
  const cols = rows > 0 ? grid[0].length : 0;
  return {
    rows,
    cols,
    workGrid: grid.map((row) => [...row]),
    currentClues: clues,
    warmStartSeed: null,
    warmStartDirty: undefined,
    alpha: options?.alpha ?? 8,
    beta: options?.beta ?? 1,
    gamma: options?.gamma ?? 8,
    sourceImage: options?.sourceImage,
    existingPuzzles: options?.existingPuzzles,
  };
}

/** Advance one iteration up to (but not including) candidate scoring. */
function settleStep(state: AdaptState): AdaptStep {
  const seed = state.warmStartSeed ?? allUnknown(state.rows, state.cols);
  const settleResult = fullSettle(seed, state.currentClues, state.warmStartDirty);

  if (settleResult.remainingUnknowns === 0) {
    return { kind: 'done', sweepCount: settleResult.sweepCount };
  }

  const candidates = collectCandidates(
    state.workGrid,
    settleResult.grid,
    state.rows,
    state.cols,
  );
  if (candidates.length === 0) {
    return { kind: 'done', sweepCount: settleResult.sweepCount };
  }

  return {
    kind: 'score',
    candidates,
    settleGrid: settleResult.grid,
    sweepCount: settleResult.sweepCount,
  };
}

/** Apply the best candidate flip and warm-start the next iteration. */
function applyFlip(
  state: AdaptState,
  settleGrid: CellValue[][],
  br: number,
  bc: number,
): void {
  state.workGrid[br][bc] = true;

  const newRow = cluesFromLine(state.workGrid[br]);
  const colLine = new Array<boolean>(state.rows);
  for (let i = 0; i < state.rows; i++) colLine[i] = state.workGrid[i][bc];
  const newCol = cluesFromLine(colLine);
  state.currentClues = {
    rows: state.currentClues.rows.map((x, i) => (i === br ? newRow : x)),
    cols: state.currentClues.cols.map((x, i) => (i === bc ? newCol : x)),
  };

  state.warmStartSeed = cloneCellGrid(settleGrid);
  state.warmStartSeed[br][bc] = CellValue.Filled;
  state.warmStartDirty = { rows: [br], cols: [bc] };
}

function pickBest(scores: number[]): number {
  let bestIdx = -1;
  let bestScore = Infinity;
  for (let i = 0; i < scores.length; i++) {
    if (scores[i] < bestScore) {
      bestScore = scores[i];
      bestIdx = i;
    }
  }
  return bestIdx;
}

function runAdaptSync(
  grid: boolean[][],
  clues: PuzzleClues,
  options: AdaptOptions | undefined,
): { grid: boolean[][]; clues: PuzzleClues; sweepCount: number } {
  const state = initState(grid, clues, options);

  for (;;) {
    const step = settleStep(state);
    if (step.kind === 'done') {
      return {
        grid: state.workGrid,
        clues: state.currentClues,
        sweepCount: step.sweepCount,
      };
    }

    const ctx: AdaptScoreContext = {
      workGrid: state.workGrid,
      currentClues: state.currentClues,
      settleGrid: step.settleGrid,
      alpha: state.alpha,
      beta: state.beta,
      gamma: state.gamma,
      sourceImage: state.sourceImage,
      existingPuzzles: state.existingPuzzles,
    };

    const scores = new Array<number>(step.candidates.length);
    for (let i = 0; i < step.candidates.length; i++) {
      const [r, c] = step.candidates[i];
      scores[i] = scoreAdaptCandidate(r, c, ctx);
    }

    const bestIdx = pickBest(scores);
    if (bestIdx < 0) {
      return {
        grid: state.workGrid,
        clues: state.currentClues,
        sweepCount: step.sweepCount,
      };
    }

    const [br, bc] = step.candidates[bestIdx];
    applyFlip(state, step.settleGrid, br, bc);
  }
}

async function runAdaptAsync(
  grid: boolean[][],
  clues: PuzzleClues,
  options: AdaptOptions | undefined,
  scoreBatch: AdaptScoreBatchFn,
): Promise<{ grid: boolean[][]; clues: PuzzleClues; sweepCount: number }> {
  const state = initState(grid, clues, options);

  for (;;) {
    const step = settleStep(state);
    if (step.kind === 'done') {
      return {
        grid: state.workGrid,
        clues: state.currentClues,
        sweepCount: step.sweepCount,
      };
    }

    const { scores } = await scoreBatch({
      workGrid: state.workGrid,
      currentClues: state.currentClues,
      settleGrid: step.settleGrid,
      candidates: step.candidates,
      alpha: state.alpha,
      beta: state.beta,
      gamma: state.gamma,
      sourceImage: state.sourceImage,
      existingPuzzles: state.existingPuzzles,
    });

    const bestIdx = pickBest(scores);
    if (bestIdx < 0) {
      return {
        grid: state.workGrid,
        clues: state.currentClues,
        sweepCount: step.sweepCount,
      };
    }

    const [br, bc] = step.candidates[bestIdx];
    applyFlip(state, step.settleGrid, br, bc);
  }
}

function collectCandidates(
  workGrid: boolean[][],
  settleGrid: CellValue[][],
  rows: number,
  cols: number,
): [number, number][] {
  const out: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (workGrid[r][c]) continue;
      if (settleGrid[r][c] !== CellValue.Unknown) continue;
      out.push([r, c]);
    }
  }
  return out;
}

function allUnknown(rows: number, cols: number): CellValue[][] {
  return Array.from({ length: rows }, () =>
    new Array<CellValue>(cols).fill(CellValue.Unknown),
  );
}

function cloneCellGrid(g: CellValue[][]): CellValue[][] {
  return g.map((row) => [...row]);
}
